import { setSessionAttribute } from "./sessionAttributes";

let listener: (() => void) | undefined;
let hiddenDuringSession = false;

/**
 * Stamps tab visibility as session attributes so backgrounded sessions can be excluded from
 * perceived-latency percentiles. A backgrounded tab throttles timers and inflates boot/perceived
 * durations, so those sessions must be filterable:
 *
 * - `tab.visible` — the tab's state when each span is created (`true` when foregrounded).
 * - `tab.hidden_during_session` — latches `true` once the tab has been hidden at least once (or if
 *   it loaded in the background), so a whole session's spans can be excluded even after it returns
 *   to the foreground.
 */
export function initializeTabVisibility(): void {
  if (typeof document === "undefined" || listener) return;
  const apply = () => {
    const hidden = document.visibilityState === "hidden";
    setSessionAttribute("tab.visible", !hidden);
    if (hidden) {
      hiddenDuringSession = true;
      setSessionAttribute("tab.hidden_during_session", true);
    }
  };
  // Seed the latch for tabs opened in the background (new tab, restored session).
  hiddenDuringSession = document.visibilityState === "hidden";
  setSessionAttribute("tab.hidden_during_session", hiddenDuringSession);
  apply();
  listener = apply;
  document.addEventListener("visibilitychange", listener);
}

/**
 * Current value of the hidden-during-session latch, for explicit stamping on measurements that
 * must always carry it (boot spans). Falls back to live visibility when the listener never
 * initialized, so early callers still get a truthful answer.
 */
export function wasTabHiddenDuringSession(): boolean {
  if (hiddenDuringSession) return true;
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/** Test-only teardown of the visibility listener. */
export function resetTabVisibilityForTests(): void {
  if (typeof document !== "undefined" && listener) {
    document.removeEventListener("visibilitychange", listener);
  }
  listener = undefined;
  hiddenDuringSession = false;
}
